import { Injectable } from '@angular/core';
import { environment } from '../../environments/environment';
import { LoggerService } from './logger.service';
import * as amplitude from '@amplitude/analytics-browser';

/**
 * Injectable service for initializing Amplitude analytics and tracking application events.
 * It configures Amplitude with disabled automatic tracking, provides a generic event tracking API,
 * and includes helper methods for common domain-specific analytics events.
 * @service
 * @providedIn root
 *
 * @method constructor
 * Initializes Amplitude on service creation.
 *
 * @method initializeAmplitude
 * Performs one-time Amplitude SDK initialization using the configured API key.
 *
 * @method trackEvent
 * Tracks a custom Amplitude event with optional properties and an optional user ID.
 * @param {string} eventName - The name of the event to track.
 * @param {Record<string, any>} eventProperties - Additional properties to attach to the event.
 * @param {string} [userId] - Optional user identifier to associate with the event.
 *
 * @method trackEmployeeAssigned
 * Tracks when an employee is assigned to an appointment.
 * @param {string} appointmentId - The appointment identifier.
 * @param {string} employeeId - The employee identifier.
 * @param {string} [userId] - Optional user identifier to associate with the event.
 *
 * @method trackOrderCreated
 * Tracks when a new order is created.
 * @param {string} userType - The type of user creating the order.
 * @param {string} packageId - The package identifier.
 * @param {string} locationId - The location identifier.
 * @param {string} [userId] - Optional user identifier to associate with the event.
 *
 * @method trackAnalyticsViewed
 * Tracks when analytics are viewed by a user.
 * @param {string} businessId - The business identifier.
 * @param {string} timeRange - The selected analytics time range.
 * @param {string} [userId] - Optional user identifier to associate with the event.
 *
 * @lifecycle constructor
 * Initializes analytics immediately when the service is instantiated.
 */
@Injectable({
  providedIn: 'root',
})
export class AmplitudeService {
  private isInitialized = false;

  constructor(private logger: LoggerService) {
    this.initializeAmplitude();
  }

  /**
   * Initializes the Amplitude analytics service if it has not already been initialized and an API key is available.
   * @example
   * initializeAmplitude()
   * void
   * @returns {void} No return value.
   */
  private initializeAmplitude(): void {
    if (!this.isInitialized && environment.amplitudeApiKey) {
      try {
        amplitude.init(environment.amplitudeApiKey, {
          defaultTracking: {
            sessions: false,
            pageViews: false,
            formInteractions: false,
            fileDownloads: false,
          },
        });
        this.isInitialized = true;
        this.logger.debug('Amplitude initialized');
      } catch (error) {
        this.logger.error('Failed to initialize Amplitude', error);
      }
    }
  }

  /**
   * Tracks an Amplitude event with optional event properties and user ID.
   * @example
   * trackEvent('Button Clicked', { buttonName: 'signup' }, 'user-123')
   * Promise<void>
   * @param {string} eventName - Name of the event to track.
   * @param {Record<string, any>} eventProperties - Key-value pairs of additional event properties.
   * @param {string} [userId] - Optional user ID to associate with the event.
   * @returns {Promise<void>} Resolves when the event has been processed or skips if not initialized.
   **/
  async trackEvent(
    eventName: string,
    eventProperties: Record<string, any> = {},
    userId?: string
  ): Promise<void> {
    try {
      if (!this.isInitialized) {
        this.logger.warn('Amplitude not initialized, skipping event', eventName);
        return;
      }

      if (userId) {
        amplitude.setUserId(userId);
      }

      amplitude.track(eventName, eventProperties);
    } catch (error) {
      this.logger.error(`Failed to track Amplitude event: ${eventName}`, error);
    }
  }

  trackEmployeeAssigned(appointmentId: string, employeeId: string, userId?: string): void {
    this.trackEvent(
      'Employee Assigned to Appointment',
      {
        appointment_id: appointmentId,
        employee_id: employeeId,
        source: 'Web',
        timestamp: new Date().toISOString(),
      },
      userId
    );
  }

  trackOrderCreated(
    userType: string,
    packageId: string,
    locationId: string,
    userId?: string
  ): void {
    this.trackEvent(
      'Order Created',
      {
        user_type: userType,
        package_id: packageId,
        location_id: locationId,
      },
      userId
    );
  }

  trackAnalyticsViewed(businessId: string, timeRange: string, userId?: string): void {
    this.trackEvent(
      'Analytics Viewed',
      {
        business_id: businessId,
        time_range: timeRange,
      },
      userId
    );
  }

  /**
   * General Sales Report admin actions (CWS-1230, PRD §31).
   *
   * `action` distinguishes the tracked interactions the PRD lists — opened,
   * date range changed, location filter changed, comparison enabled, filters
   * applied, drill-down opened, CSV/PDF export clicked.
   */
  trackGeneralSalesReport(
    action: string,
    properties: Record<string, unknown> = {},
    userId?: string
  ): void {
    this.trackEvent(
      'general_sales_report_viewed',
      {
        action,
        ...properties,
      },
      userId
    );
  }
}
